import {
  getBookingWeekdayName,
  parseCalendarLocalDateTime,
  formatBookingTimeLabel,
} from "./bookingDateTime.js";

function parseWorkingTime(timeStr) {
  const [hoursPart, minutesPart] = (timeStr || "00:00").split(":");
  return {
    hours: parseInt(hoursPart, 10) || 0,
    minutes: parseInt(minutesPart, 10) || 0,
    totalMinutes:
      (parseInt(hoursPart, 10) || 0) * 60 + (parseInt(minutesPart, 10) || 0),
  };
}

function toDateTime(baseDate, totalMinutes) {
  const dt = new Date(baseDate);
  dt.setHours(0, 0, 0, 0);
  dt.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return dt;
}

function sameCalendarDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function intervalsOverlap(startA, endA, startB, endB) {
  return startA < endB && endA > startB;
}

function coerceEventDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const calendarLocal = parseCalendarLocalDateTime(value);
    if (calendarLocal) return calendarLocal;
    const iso = new Date(value);
    return Number.isNaN(iso.getTime()) ? null : iso;
  }
  return null;
}

function getBookedIntervalsForDate(date, bookedEvents) {
  const intervals = [];
  for (const event of bookedEvents || []) {
    const start = coerceEventDate(event?.start);
    const end = coerceEventDate(event?.end);
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      continue;
    }
    if (!sameCalendarDay(start, date) && !sameCalendarDay(end, date)) {
      continue;
    }
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    const clampedStart = start < dayStart ? dayStart : start;
    const clampedEnd = end > dayEnd ? dayEnd : end;
    if (clampedEnd > clampedStart) {
      intervals.push({ start: clampedStart, end: clampedEnd });
    }
  }
  return intervals;
}

function isSlotBooked(slotStart, slotEnd, bookedIntervals) {
  return bookedIntervals.some(({ start, end }) =>
    intervalsOverlap(slotStart, slotEnd, start, end)
  );
}

function formatSlotParts(startDate, endDate) {
  const startLabel = formatBookingTimeLabel(startDate);
  const endLabel = formatBookingTimeLabel(endDate);
  return {
    startLabel,
    label: `${startLabel} – ${endLabel}`,
  };
}

export function getWorkingHoursForDate(date, workingHoursArray) {
  if (!date || !Array.isArray(workingHoursArray) || !workingHoursArray.length) {
    return { starttime: "09:00", endtime: "17:00" };
  }
  const dayName = getBookingWeekdayName(date);
  return (
    workingHoursArray.find((d) => d.day === dayName) || {
      starttime: "09:00",
      endtime: "17:00",
    }
  );
}

export function isUnavailableWorkingDay(date, workingHoursArray) {
  if (!date) return true;
  const hours = getWorkingHoursForDate(date, workingHoursArray);
  return hours.starttime === "00:00" && hours.endtime === "00:00";
}

export function getAvailableHourSlots({
  date,
  workingHoursArray,
  bookedEvents = [],
  earliestDateTime = null,
  maxSlots = 8,
  slotDurationMinutes = 60,
  stepMinutes = 60,
}) {
  if (!date || isUnavailableWorkingDay(date, workingHoursArray)) {
    return [];
  }

  const workingHours = getWorkingHoursForDate(date, workingHoursArray);
  const opening = parseWorkingTime(workingHours.starttime);
  const closing = parseWorkingTime(workingHours.endtime);
  const bookedIntervals = getBookedIntervalsForDate(date, bookedEvents);
  const slots = [];

  for (
    let startMin = opening.totalMinutes;
    startMin + slotDurationMinutes <= closing.totalMinutes;
    startMin += stepMinutes
  ) {
    const slotStart = toDateTime(date, startMin);
    const slotEnd = toDateTime(date, startMin + slotDurationMinutes);

    if (earliestDateTime && slotStart < earliestDateTime) {
      continue;
    }

    if (isSlotBooked(slotStart, slotEnd, bookedIntervals)) {
      continue;
    }

    slots.push({
      id: `${startMin}-${startMin + slotDurationMinutes}`,
      startHour: Math.floor(startMin / 60),
      startMinute: startMin % 60,
      endHour: Math.floor((startMin + slotDurationMinutes) / 60),
      endMinute: (startMin + slotDurationMinutes) % 60,
      ...formatSlotParts(slotStart, slotEnd),
    });

    if (slots.length >= maxSlots) {
      break;
    }
  }

  return slots;
}

export function findFirstDateWithAvailableSlots({
  fromDate,
  maxDays = 21,
  workingHoursArray,
  bookedEvents,
  earliestDateTime,
  maxDate = null,
}) {
  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);

  for (let offset = 0; offset < maxDays; offset += 1) {
    const candidate = new Date(start);
    candidate.setDate(start.getDate() + offset);

    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(23, 59, 59, 999);
      if (candidate > max) {
        break;
      }
    }

    if (isUnavailableWorkingDay(candidate, workingHoursArray)) {
      continue;
    }

    const earliestForDay =
      earliestDateTime && sameCalendarDay(earliestDateTime, candidate)
        ? earliestDateTime
        : null;

    const slots = getAvailableHourSlots({
      date: candidate,
      workingHoursArray,
      bookedEvents,
      earliestDateTime: earliestForDay,
      maxSlots: 1,
    });

    if (slots.length > 0) {
      return candidate;
    }
  }

  for (let offset = 0; offset < maxDays; offset += 1) {
    const candidate = new Date(start);
    candidate.setDate(start.getDate() + offset);

    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(23, 59, 59, 999);
      if (candidate > max) {
        break;
      }
    }

    if (!isUnavailableWorkingDay(candidate, workingHoursArray)) {
      return candidate;
    }
  }

  return start;
}
